import { useState, useEffect, useCallback, useRef } from 'react';
import type { ImportJob } from '@/types/import';
import { ImportStatus } from '@/types/import';
import { importService } from '@/services/import-service';

export function useImportJobs(pollingInterval = 30000) {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const consecutiveFailures = useRef(0);
  const backoffDelay = useRef(0);

  const fetchJobs = useCallback(async (): Promise<void> => {
    try {
      const response = await importService.getJobs();
      setJobs(response.jobs);
      setError(null);
      setBackendAvailable(true);
      consecutiveFailures.current = 0;
      backoffDelay.current = 0;
    } catch (err) {
      consecutiveFailures.current++;
      
      if (consecutiveFailures.current > 3) {
        setBackendAvailable(false);
        backoffDelay.current = Math.min(5000 * Math.pow(2, consecutiveFailures.current - 4), 60000);
      }
      
      // Silently fail if API is not available
      setJobs([]);
      setError(null);
      
      if (consecutiveFailures.current === 1 || consecutiveFailures.current % 10 === 0) {
        console.warn(`Import API not available (attempt ${consecutiveFailures.current}):`, err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const hasActiveJobs = jobs.some(
      (job) => job.status === ImportStatus.Processing
    );

    if (!hasActiveJobs && backendAvailable) {
      return;
    }

    const effectiveInterval = backendAvailable 
      ? pollingInterval 
      : Math.max(pollingInterval, backoffDelay.current);

    const interval = setInterval(fetchJobs, effectiveInterval);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs, pollingInterval, backendAvailable]);

  const activeJobs = jobs.filter(
    (job) => job.status === ImportStatus.Processing
  );

  const completedJobs = jobs.filter(
    (job) => job.status === ImportStatus.Completed
  );

  const failedJobs = jobs.filter(
    (job) => job.status === ImportStatus.Failed
  );

  return {
    jobs,
    activeJobs,
    completedJobs,
    failedJobs,
    loading,
    error,
    refetch: fetchJobs,
  };
}
